import styled from "@emotion/styled"
import { Button, IconButton, Tab, Tabs } from "@mui/material"
import { useTranslations } from "next-intl"
import React, { useRef, useState } from "react"
import { CalendarIcon, NewText, PhoneIcon, useIsDesktop, XIcon } from "ui"
import { Apartment, useOutsideClick } from "utils"
import { choosenProject, submenuClick } from "utils/gtm"
import { ProjectsMessages } from "../../Projects"
import AccordionInnerDetails from "./../AccordionInnerDetails"
import { a11yProps } from "./../Apartments"
import DrawerContactForm from "./DrawerContactForm"
import DrawerGallery from "./DrawerGallery"
import { FormData } from "./PaymentForm"

const Container = styled.div`
  * {
    font-family: inherit !important;
  }
  --text-black: #011727;
  padding-top: 96px;
  padding-bottom: 72px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  position: relative;
  flex: 1;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 80px;
  }
`

const StyledInner = styled.div`
  gap: 24px;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  flex: 1;
  height: 1px;
  background-color: #fff;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 20px 33px;
  }
`
const TabsContainer = styled(Tabs)`
  width: 100%;
  overflow: visible;
  .MuiTabs-flexContainer {
    > * {
      flex-shrink: 0;
    }
    border-radius: 12px;
    padding: 8px;
    justify-content: space-between;
    min-width: 100%;
    width: max-content;
    background-color: #f7f7f7;
    display: flex;
    margin-inline: 20px;
    ${(props) => props.theme.breakpoints.up("md")} {
      margin-inline: 0;
    }
  }
  .MuiTabs-scroller {
    overflow: auto !important;
  }
`

const TabsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
`

const StyledTab = styled(Tab)<{ active: boolean }>`
  font-family: var(--narkis) !important;
  * {
    font-family: inherit !important;
  }
  min-height: 0;
  padding: 8px 8px;
  min-width: 0;
  border-radius: 8px;
  font-size: ${({ active }) => (active ? "18px" : "16px")};
  line-height: ${({ active }) => (active ? "24px" : "145%")};
  color: ${({ active }) => (active ? "#fff !important" : "#011727 !important")};
  font-weight: ${({ active }) => (active ? "700" : "300")};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.blue4rentMarketing : "transparent"};
`

const StyledBtnsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  width: 100%;
  gap: 10px;
  flex-shrink: 0;
  height: 72px;
  padding: 16px 20px;
  z-index: 3;
  > * {
    flex: 1;
    width: 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 16px;
    height: 80px;
    gap: 16px;
  }
`

const StyledButton = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 5px;
  justify-content: center;
`

const WhiteStyledButton = styled(StyledButton)`
  --theme-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  align-items: center;
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
  background-color: white;
  gap: 4px;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    color: var(--theme-color);
    border-color: var(--theme-color);
    background-color: transparent;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    border: 1px solid white;
    color: var(--theme-color);
    &:hover {
      color: white;
      border-color: white;
      background-color: transparent;
    }
  }
`

const BlueStyledButton = styled(StyledButton)`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const IWantRentBtn = styled(BlueStyledButton)`
  display: flex;
`
const StyledActive = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding-inline: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: 0;
  }
`

const DrawerHeader = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  height: 96px;
  width: 100%;
  padding: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 33px;
    padding-top: 20px;
  }
`
const DrawerHeaderTexts = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  gap: 20px;
  width: 100%;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  gap: 8px;
  > * {
    /* flex */
  }
`

const RentRangePriceText = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
  margin: 0;
  font-size: 18px;
  font-weight: 250;
  line-height: 150%;
  letter-spacing: -0.36px;
  order: -1;
  color: white;
  ${(props) => props.theme.breakpoints.up("md")} {
    order: unset;
    color: var(--text-black);
  }
`

const RentRangePriceStrong = styled.strong`
  margin: 0;
  font-weight: 400;
  line-height: 150%;
  font-size: 30px;
  color: inherit;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
  }
`

const ScrollableContent = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 1px;
  flex: 1;
`

const PaddingBottom = styled.div<{ padding: number }>`
  padding-bottom: ${(props) => props.padding || 0}px;
`

const StyledActionButtons = styled.div`
  padding-inline: 20px;
  gap: 20px;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
`

type TabProps = {
  value: number
  index: number
  apartment: Apartment
  projectPage?: boolean
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  buildingCharacteristicsText?: string
}
export type TabNameType = "plan" | "specs" | "docs" | "gallery" | "rentplan"
export type NewTabNameType =
  | "buildingFeatures"
  | "apartmentFeatures"
  | "projectLocation"
  | "apartmentPlan"
  | "documents"

function ActiveTab({ index, value, apartment, buildingCharacteristicsText }: TabProps) {
  const getNewType = (): NewTabNameType => {
    switch (index) {
      case 0:
        return "buildingFeatures"
      case 1:
        return "apartmentFeatures"
      case 2:
        return "projectLocation"
      case 3:
        return "apartmentPlan"
      case 4:
        return "documents"
      default:
        return "buildingFeatures"
    }
  }
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      <PaddingBottom padding={40}>
        {value === index && (
          <AccordionInnerDetails
            apartment={apartment}
            buildingCharacteristicsText={buildingCharacteristicsText}
            type={getNewType()}
            forDrawer
          />
        )}
      </PaddingBottom>
    </div>
  )
}

type Props = {
  apartment: Apartment
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  bmbyId?: number
  status?: any
  projectPage?: boolean
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  companyId?: number
  // eslint-disable-next-line no-unused-vars
  handleDrawerClose: (_event: React.MouseEvent<HTMLButtonElement>) => void
  buildingCharacteristicsText?: string
  locale: string
  bmbyMeetingId?: string | number
  fontFamilyVariable?: string
  phone?: string
  rentProjectInterestedNum?: number
}

function DrawerInner({
  apartment,
  formSubjects,
  messages,
  status,
  projectPage,
  imageGallery,
  companyId,
  handleDrawerClose,
  buildingCharacteristicsText,
  locale,
  bmbyMeetingId,
  fontFamilyVariable,
  phone,
  rentProjectInterestedNum,
}: Props) {
  const isDesktop = useIsDesktop()
  const t = useTranslations("AccordionDetails")
  const [value, setValue] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)

  const formRef = useRef(null)

  const handleFormClose = () => {
    setDrawerOpen(false)
  }
  const handleFormOpen = () => {
    choosenProject("מעוניינ/ת בדירה", apartment?.propertyTitle)
    submenuClick("מעוניינ/ת בדירה", apartment?.propertyTitle)
    setDrawerOpen(true)
  }

  const handleScheduleBmbyMeeting = () => {
    const lang = locale === "en" ? "en" : "he"
    const url = `https://bmbyscheduler.azurewebsites.net/?meeting=${bmbyMeetingId}&lang=${lang}`
    const popup = window.open(url, "bmbyScheduler", "width=940,height=500")
    if (!popup) return // in case popup was blocked

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer) // ✅ cleanup
        // success logic here
      }
    }, 500)

    // ✅ extra safety: cleanup if user leaves page
    window.addEventListener("beforeunload", () => clearInterval(timer))
  }

  useOutsideClick([formRef], handleFormClose)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    submenuClick(
      newValue === 0 ? "תוכנית דירה" : newValue === 1 ? "מפרט דירה" : "מסמכים וחוזים",
      apartment?.propertyTitle,
    )
    setValue(newValue)
  }
  const formData: FormData = {
    price: apartment.price,
    currency: apartment.priceCurrency,
    area: apartment.areaInSquareMeters,
    floor: apartment.floor,
    rooms: apartment.numberOfRooms,
    assetName: apartment.assetName,
    buildingName: apartment.buildingTitle,
    apartmentNumber: apartment.apartmentNumber,
  }
  const galleryImages = imageGallery?.apartmentGallery ?? []

  const isTheApartmentTaken = apartment.moreInfoContentHTML === "משוריין"
  return (
    <Container className={fontFamilyVariable && fontFamilyVariable}>
      {/* header */}
      <DrawerHeader>
        <DrawerHeaderTexts>
          <NewText
            component="p"
            desktopFontSize={32}
            lineHeight={1.1}
            fontWeight={400}
            color="inherit"
            sx={{
              margin: 0,
              padding: 0,
            }}
          >
            {t("roomCountApartment", { count: apartment?.numberOfRooms })}
          </NewText>
          <NewText
            component="p"
            desktopFontSize={24}
            desktopLineHeight={"28px"}
            fontWeight={400}
            mobileFontSize={20}
            color="inherit"
            sx={{
              margin: 0,
              padding: 0,
            }}
          >
            {apartment?.address}
          </NewText>
          {isDesktop && (
            <RentRangePriceText>
              {t.rich("pricePerMonth", {
                strong: (chunks) => <RentRangePriceStrong>{chunks}</RentRangePriceStrong>,
                price:
                  typeof apartment?.price === "number"
                    ? apartment.price.toLocaleString()
                    : apartment?.price ?? "__",
                currency: apartment?.priceCurrency ?? "₪",
              })}
            </RentRangePriceText>
          )}
        </DrawerHeaderTexts>
        <IconButton
          sx={{
            flexShrink: 0,
            marginInlineStart: "10px",
            borderRadius: "50%",
            border: "1px solid #F7F7F7",
            background: "rgba(255, 255, 255, 0.50)",
          }}
          onClick={handleDrawerClose}
        >
          <XIcon width={20} height={20} />
        </IconButton>
      </DrawerHeader>
      {/* scrollable content - tabs */}
      <ScrollableContent>
        {galleryImages.length > 0 && (
          <DrawerGallery
            locale={locale}
            images={galleryImages}
            expandIcon
            rentProjectInterestedNum={rentProjectInterestedNum}
          />
        )}
        <StyledInner>
          <TabsContent>
            <TabsContainer value={value} onChange={handleChange} aria-label="property types">
              <StyledTab
                active={0 === value}
                key={0}
                label={t("buildingFeatures")}
                {...a11yProps(0)}
              />
              <StyledTab
                active={1 === value}
                key={1}
                label={t("apartmentFeatures")}
                {...a11yProps(1)}
              />
              <StyledTab
                active={2 === value}
                key={2}
                label={t("projectLocation")}
                {...a11yProps(2)}
              />
              <StyledTab
                active={3 === value}
                key={3}
                label={t("apartmentPlan")}
                {...a11yProps(3)}
              />
              <StyledTab
                active={4 === value}
                key={4}
                label={t("agreementDocs")}
                {...a11yProps(4)}
              />
            </TabsContainer>
          </TabsContent>
          <StyledActive>
            {Array.from([0, 1, 2, 3, 4]).map((el, i) => (
              <ActiveTab
                key={i}
                index={i}
                value={value}
                apartment={apartment}
                projectPage={projectPage}
                imageGallery={imageGallery}
                buildingCharacteristicsText={buildingCharacteristicsText}
              />
            ))}
          </StyledActive>
          {!isDesktop && (
            <StyledActionButtons>
              {bmbyMeetingId && (
                <WhiteStyledButton onClick={handleScheduleBmbyMeeting} variant="outlined">
                  <CalendarIcon color="inherit" fill="currentColor" width={20} height={20} />
                  {t("scheduleAppointment")}
                </WhiteStyledButton>
              )}
              <WhiteStyledButton
                href={phone}
                disabled={!phone}
                LinkComponent={"a"}
                variant="outlined"
              >
                <PhoneIcon color="inherit" fill="currentColor" width={20} height={20} />
                {t("callNow")}
              </WhiteStyledButton>
              <div style={{ height: "10px", flexShrink: 0 }} />
            </StyledActionButtons>
          )}
        </StyledInner>
      </ScrollableContent>
      {/* buttons */}
      <StyledBtnsWrapper>
        {!isTheApartmentTaken && (
          <IWantRentBtn variant="outlined" onClick={() => handleFormOpen()}>
            {t("iWantRent")}
          </IWantRentBtn>
        )}
        {!isDesktop ? (
          <RentRangePriceText>
            {t.rich("pricePerMonth", {
              strong: (chunks) => <RentRangePriceStrong>{chunks}</RentRangePriceStrong>,
              price: apartment?.price ?? "__",
              currency: apartment?.priceCurrency ?? "₪",
            })}
          </RentRangePriceText>
        ) : (
          <>
            {bmbyMeetingId && (
              <WhiteStyledButton onClick={handleScheduleBmbyMeeting} variant="outlined">
                <CalendarIcon color="inherit" fill="currentColor" width={20} height={20} />
                {t("scheduleAppointment")}
              </WhiteStyledButton>
            )}
            <WhiteStyledButton
              href={phone}
              disabled={!phone}
              LinkComponent={"a"}
              variant="outlined"
            >
              <PhoneIcon color="inherit" fill="currentColor" width={20} height={20} />
              {t("callNow")}
            </WhiteStyledButton>
          </>
        )}
      </StyledBtnsWrapper>
      <DrawerContactForm
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formProps={{
          formData,
          messages,
          formSubjects,
          onClose: handleFormClose,
          bmbyId: apartment.leadFormBMBYId,
          status: status,
          companyId: companyId,
          newStyle: true,
        }}
        paymentForm
      />
    </Container>
  )
}

export default DrawerInner
